import { brandToken, type BrandKit } from '../brand';

/**
 * Colours and fonts of a generated video: a ready palette, or one derived from a brand kit so
 * that every pairing stays readable (text on backgrounds, the button label on the button).
 * Colours taken straight from the brand are written as brand references, so the template
 * follows the brand if it changes later.
 */

export interface GenPalette {
  id: string;
  /** Dark scenes: gradient from `dark` to `dark2`. */
  dark: string;
  dark2: string;
  /** Light scenes: gradient from `light` to `paper`. */
  light: string;
  paper: string;
  /** Text on light scenes. */
  ink: string;
  /** Secondary text on dark scenes. */
  mutedDark: string;
  /** Secondary text on light scenes. */
  mutedLight: string;
  /** Highlights on dark scenes: accent words, buttons, bars. */
  accent: string;
  /** Text on `accent`. */
  onAccent: string;
  /** Highlights on light scenes (readable on `light`). */
  primary: string;
  /** Text on `primary`. */
  onPrimary: string;
  /** A few extra colours for cards and bars. */
  extras: string[];
  heading: string;
  body: string;
}

export const GEN_PALETTES: GenPalette[] = [
  {
    id: 'indigo',
    dark: '#1E1B4B',
    dark2: '#4338CA',
    light: '#EEF2FF',
    paper: '#FFFFFF',
    ink: '#18181B',
    mutedDark: '#C7D2FE',
    mutedLight: '#52525B',
    accent: '#FACC15',
    onAccent: '#1E1B4B',
    primary: '#4F46E5',
    onPrimary: '#FFFFFF',
    extras: ['#E11D48', '#0D9488', '#EA580C'],
    heading: 'Montserrat',
    body: 'Inter',
  },
  {
    id: 'ember',
    dark: '#1C1917',
    dark2: '#7C2D12',
    light: '#FFF7ED',
    paper: '#FFFFFF',
    ink: '#1C1917',
    mutedDark: '#FED7AA',
    mutedLight: '#57534E',
    accent: '#FB923C',
    onAccent: '#1C1917',
    primary: '#C2410C',
    onPrimary: '#FFFFFF',
    extras: ['#B45309', '#15803D', '#9F1239'],
    heading: 'Unbounded',
    body: 'Manrope',
  },
  {
    id: 'forest',
    dark: '#052E2B',
    dark2: '#0F766E',
    light: '#F0FDF4',
    paper: '#FFFFFF',
    ink: '#052E16',
    mutedDark: '#A7F3D0',
    mutedLight: '#3F3F46',
    accent: '#A3E635',
    onAccent: '#052E16',
    primary: '#047857',
    onPrimary: '#FFFFFF',
    extras: ['#0369A1', '#B45309', '#7C3AED'],
    heading: 'Rubik',
    body: 'Inter',
  },
  {
    id: 'plum',
    dark: '#2E1065',
    dark2: '#86198F',
    light: '#FDF4FF',
    paper: '#FFFFFF',
    ink: '#1E1B4B',
    mutedDark: '#F5D0FE',
    mutedLight: '#52525B',
    accent: '#F472B6',
    onAccent: '#2E1065',
    primary: '#A21CAF',
    onPrimary: '#FFFFFF',
    extras: ['#7C3AED', '#DB2777', '#0891B2'],
    heading: 'Raleway',
    body: 'Nunito',
  },
  {
    id: 'ocean',
    dark: '#082F49',
    dark2: '#0369A1',
    light: '#F0F9FF',
    paper: '#FFFFFF',
    ink: '#0C1A2B',
    mutedDark: '#BAE6FD',
    mutedLight: '#475569',
    accent: '#38BDF8',
    onAccent: '#082F49',
    primary: '#0284C7',
    onPrimary: '#FFFFFF',
    extras: ['#0D9488', '#6366F1', '#F59E0B'],
    heading: 'Golos Text',
    body: 'Inter',
  },
  {
    id: 'mono',
    dark: '#09090B',
    dark2: '#27272A',
    light: '#F4F4F5',
    paper: '#FFFFFF',
    ink: '#09090B',
    mutedDark: '#D4D4D8',
    mutedLight: '#52525B',
    accent: '#FFFFFF',
    onAccent: '#09090B',
    primary: '#09090B',
    onPrimary: '#FFFFFF',
    extras: ['#3F3F46', '#71717A', '#52525B'],
    heading: 'Oswald',
    body: 'Inter',
  },
];

const parse = (hex: string): [number, number, number] => {
  let h = hex.replace('#', '');
  if (h.length === 3 || h.length === 4) h = [...h.slice(0, 3)].map((c) => c + c).join('');
  const n = parseInt(h.slice(0, 6), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const toHex = (rgb: number[]) =>
  '#' +
  rgb
    .map((v) =>
      Math.round(Math.max(0, Math.min(255, v)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')
    .toUpperCase();

/** Mix two colours: `k` of `b` into `a`. */
export function mix(a: string, b: string, k: number): string {
  const x = parse(a);
  const y = parse(b);
  return toHex(x.map((v, i) => v + (y[i] - v) * k));
}

/** WCAG relative luminance. */
export function luminance(hex: string): number {
  const [r, g, b] = parse(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrast(a: string, b: string): number {
  const [l1, l2] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (l1 + 0.05) / (l2 + 0.05);
}

/** The more readable of two text colours on `bg`. */
const textOn = (bg: string, dark: string, light = '#FFFFFF') =>
  contrast(bg, light) >= contrast(bg, dark) ? light : dark;

/** Push `c` darker or lighter until it reads on `bg` (contrast ≥ min). */
function readable(c: string, bg: string, min: number): string {
  if (contrast(c, bg) >= min) return c;
  const towards = luminance(bg) > 0.4 ? '#000000' : '#FFFFFF';
  for (let k = 0.15; k <= 1; k += 0.15) {
    const v = mix(c, towards, k);
    if (contrast(v, bg) >= min) return v;
  }
  return towards;
}

const HEX = /^#[0-9a-fA-F]{3,8}$/;

/** A palette for a brand kit: its colours where they read, derived ones elsewhere. */
export function paletteFromBrand(kit: BrandKit, base: GenPalette = GEN_PALETTES[0]): GenPalette {
  const c = kit.colors;
  const ok = (v?: string) => (v && HEX.test(v) ? v : undefined);
  const primary = ok(c.primary) ?? ok(c.secondary) ?? base.primary;
  const accent0 = ok(c.accent) ?? ok(c.secondary) ?? base.accent;
  // Dark scenes are the primary colour taken deep; light ones its faint tint (or the brand's
  // own background when it is light)
  const dark = luminance(primary) < 0.06 ? primary : mix(primary, '#000000', 0.62);
  const dark2 =
    luminance(primary) < 0.06 ? mix(primary, '#FFFFFF', 0.12) : mix(primary, '#000000', 0.25);
  const bg = ok(c.background);
  const light = bg && luminance(bg) > 0.75 ? bg : mix(primary, '#FFFFFF', 0.92);
  const text = ok(c.text);
  const ink = text && contrast(text, light) >= 7 ? text : '#18181B';
  const accent = readable(accent0, dark, 3);
  const onLight = readable(primary, light, 3.2);
  const tok = (slot: 'primary' | 'accent', value: string, original?: string) =>
    original && value.toUpperCase() === original.toUpperCase() ? brandToken(slot, value) : value;
  return {
    id: 'brand',
    dark,
    dark2,
    light,
    paper: '#FFFFFF',
    ink,
    mutedDark: mix('#FFFFFF', primary, 0.25),
    mutedLight: mix(ink, '#FFFFFF', 0.3),
    accent: tok('accent', accent, ok(c.accent)),
    onAccent: textOn(accent, ink),
    primary: tok('primary', onLight, ok(c.primary)),
    onPrimary: textOn(onLight, ink),
    extras: [
      readable(ok(c.secondary) ?? mix(primary, '#E11D48', 0.5), light, 3),
      readable(mix(primary, '#0D9488', 0.5), light, 3),
      readable(mix(accent0, '#EA580C', 0.4), light, 3),
    ],
    heading: kit.fonts['font-heading']?.family
      ? brandToken('font-heading', kit.fonts['font-heading'].family)
      : base.heading,
    body: kit.fonts['font-body']?.family
      ? brandToken('font-body', kit.fonts['font-body'].family)
      : base.body,
  };
}

/** The plain colour behind a brand reference (`brand:accent|#FACC15` → `#FACC15`). */
export const plain = (v: string) => (v.startsWith('brand:') ? v.slice(v.indexOf('|') + 1) : v);
